import React from 'react';
import './mini-recording-up.scss';
import axios from 'axios';
import { Toast, Modal, Progress } from 'antd-mobile';
import { ScrollToTopOnMount } from "../../components/top";
import Back from "../../components/back/index";
import * as PostService from "../../service/post";

const wx = require('weixin-js-sdk');
const alert = Modal.alert;

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB（与 /aliyun/ossconfig/call_record 后端策略一致）
const ALLOWED_EXTENSIONS = ['mp3', 'm4a', 'wav'];

/**
 * 用 HTMLAudioElement 解析音频时长（秒）。读不到时返回 0，最长等 5s
 */
function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    let resolved = false;
    const url = URL.createObjectURL(file);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    const done = (sec: number) => {
      if (resolved) return;
      resolved = true;
      try { URL.revokeObjectURL(url); } catch (e) {}
      resolve(sec);
    };
    audio.onloadedmetadata = () => {
      const d = audio.duration;
      done(isFinite(d) && d > 0 ? Math.round(d) : 0);
    };
    audio.onerror = () => done(0);
    setTimeout(() => done(0), 5000);
    audio.src = url;
  });
}

interface Props {}

interface State {
  showPage: boolean;
  isMiniProgram: boolean;
  fileUping: boolean;
  completeNum: number;
  uploadSpeed: string;
  fileSize: string;
  uploadDone: boolean;
  loginChecked: boolean;
}

/**
 * 给小程序上传录音文件使用（mp3/m4a/wav）
 * 由 H5 选择本地文件并直传 OSS，再通过 wx.miniProgram.postMessage + navigateBack
 * 把 record_file / bridge_duration / end_time / unique_id 回传给小程序，
 * 字段名直接对齐后端 uploadCallRecord 接口。
 */
export class MiniRecordingUp extends React.PureComponent<Props, State> {

  uploadStartTime = 0;
  lastProgressTime = 0;
  lastProgressLoaded = 0;

  constructor(p: any) {
    super(p);
    this.state = {
      showPage: true,
      isMiniProgram: false,
      fileUping: false,
      completeNum: 0,
      uploadSpeed: '',
      fileSize: '',
      uploadDone: false,
      loginChecked: false,
    };
  }

  async componentDidMount(): Promise<void> {
    if (wx && wx.miniProgram && wx.miniProgram.getEnv) {
      wx.miniProgram.getEnv((res: any) => {
        if (res && res.miniprogram) {
          this.setState({ isMiniProgram: true });
        }
      });
    }
    // eagerly 检查登录：未登录时 PostService.checkLogin(true) 会自动跳到 /api/user/oauth
    // 走完微信公众号 OAuth 后会带 cookie 回到本页，避免「选完文件才发现没登录」的尴尬
    Toast.loading('加载中...', 0);
    try {
      await PostService.checkLogin(true);
    } catch (e) {
      console.error('checkLogin error', e);
    }
    Toast.hide();
    this.setState({ loginChecked: true });
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)}GB`;
  }

  // 选中文件回调
  fileChange = async (event: any) => {
    if (this.state.fileUping) {
      Toast.fail('正在上传');
      return;
    }
    const files = event && event.currentTarget && event.currentTarget.files;
    if (!files || files.length === 0) {
      Toast.fail('没有选取文件');
      return;
    }
    const file: File = files[0];
    const fileName = file.name || '';
    const ext = (fileName.split('.').pop() || '').toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      Toast.fail('仅支持 MP3、M4A、WAV 格式');
      try { event.target.value = ''; } catch (e) {}
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert('录音文件过大', (<>
        <p>当前文件过大（{this.formatFileSize(file.size)}），无法上传。</p>
      </>), [
        { text: '我知道了', onPress: () => {} },
      ]);
      try { event.target.value = ''; } catch (e) {}
      return;
    }

    await this.uploadFile(file, ext);
    try { event.target.value = ''; } catch (e) {}
  };

  uploadFile = async (file: File, ext: string) => {
    Toast.loading('准备上传...', 0);
    // 并行：拉 OSS 配置 + 抽音频时长（小文件秒级，大文件靠 5s 超时兜底）
    const [config, duration] = await Promise.all([
      PostService.getCallRecordOssConfig(),
      getAudioDuration(file),
    ]);
    Toast.hide();

    if (!config || !config.policy || !config.signature) {
      Toast.fail('获取上传配置失败');
      return;
    }

    const fileName = `${config.file_id}${Date.now()}.${ext}`;
    const objectKey = config.path + fileName;
    const finalUrl = config.url + fileName;

    // 构造 OSS 上传地址：bucket.endpoint_fast
    let host = config.endpoint_fast || '';
    host = host.replace(/^https?:\/\//i, '');
    if (host.indexOf(config.bucket_name) < 0) {
      host = `${config.bucket_name}.${host}`;
    }
    const uploadUrl = `https://${host}`;

    const formData = new FormData();
    formData.append('key', objectKey);
    formData.append('policy', config.policy);
    formData.append('OSSAccessKeyId', config.access_key);
    formData.append('success_action_status', '200');
    formData.append('signature', config.signature);
    formData.append('file', file);

    this.uploadStartTime = Date.now();
    this.lastProgressTime = Date.now();
    this.lastProgressLoaded = 0;
    this.setState({
      fileUping: true,
      completeNum: 0,
      uploadSpeed: '0KB/s',
      fileSize: this.formatFileSize(file.size),
      uploadDone: false,
    });

    let contentMd5 = '';
    try {
      const resp = await axios.post(uploadUrl, formData, {
        timeout: 1000 * 60 * 60,
        onUploadProgress: (e: ProgressEvent) => {
          if (!e || !e.total) return;
          const percent = Number(((e.loaded / e.total) * 100).toFixed(1));
          const now = Date.now();
          const timeDiff = (now - this.lastProgressTime) / 1000;
          let speed = this.state.uploadSpeed || '计算中...';
          if (timeDiff > 0.5) {
            const bytesPerSec = (e.loaded - this.lastProgressLoaded) / timeDiff;
            speed = `${this.formatFileSize(bytesPerSec)}/s`;
            this.lastProgressTime = now;
            this.lastProgressLoaded = e.loaded;
          }
          this.setState({
            completeNum: percent,
            uploadSpeed: speed,
          });
        },
      });
      // 从 OSS 响应里取 Content-MD5（base64）作为 unique_id
      // 浏览器能否读到取决于 OSS bucket CORS 的 ExposeHeader 配置
      const headers = (resp && resp.headers) || {};
      contentMd5 = (headers['content-md5'] || headers['Content-MD5'] || '').toString();
      if (!contentMd5) {
        console.warn('[OSS upload] Content-MD5 不可读，请确认 bucket CORS ExposeHeader 包含 Content-MD5');
      }
    } catch (e) {
      this.setState({ fileUping: false });
      Toast.fail('上传失败，请重试');
      return;
    }

    this.setState({
      fileUping: false,
      completeNum: 100,
      uploadDone: true,
    });

    // 把上传成功结果回传给小程序，再返回上一页
    if (this.state.isMiniProgram && wx && wx.miniProgram) {
      try {
        // 字段名直接对齐小程序 -> 后端 uploadCallRecord 的入参，不再做中间命名
        wx.miniProgram.postMessage({
          data: {
            type: 'recordingFile',                                                  // 路由标识，让 bindmessage 区分
            record_file: finalUrl,                                                  // OSS 完整 URL
            record_file_size: file.size,                                            // 录音文件大小（字节）
            bridge_duration: duration,                                              // 录音时长（秒），抽不到时为 0
            end_time: file.lastModified ? Math.floor(file.lastModified / 1000) : 0, // 录音时间（Unix 秒），0 表示未知
            unique_id: contentMd5,                                                  // OSS 返回的 Content-MD5（base64），可能为空
          },
        });
      } catch (e) {
        console.error('postMessage error', e);
      }
      // postMessage 数据是在「页面后退/分享/支付/组件销毁」时统一抛给小程序的 bindmessage
      // 所以这里立即调用 navigateBack 触发数据传递
      setTimeout(() => {
        try {
          wx.miniProgram.navigateBack({ delta: 1 });
        } catch (e) {
          console.error('navigateBack error', e);
        }
      }, 300);
    } else {
      Toast.success('上传成功');
    }
  };

  render() {
    const { showPage, fileUping, completeNum, uploadSpeed, fileSize, uploadDone, loginChecked } = this.state;
    let loading = (<></>);
    if (fileUping) {
      loading = (
        <div className="upload-progress-wrap">
          <div className="show-info">
            <div className="progress"><Progress percent={completeNum} position="normal"/></div>
            <div className="percent" aria-hidden="true">{completeNum}%</div>
          </div>
          <div className="meta">
            <span>{fileSize}</span>
            <span>{uploadSpeed}</span>
          </div>
          <p className="warning">上传成功前，请勿关闭或离开此页面</p>
        </div>
      );
    } else if (uploadDone) {
      loading = (
        <div className="upload-progress-wrap">
          <div className="success-tip">上传成功，正在返回小程序...</div>
        </div>
      );
    }

    let btnBox = (<></>);
    if (showPage && loginChecked && !fileUping && !uploadDone) {
      btnBox = (
        <div className="file-btn">
          <div className="upload-btn">
            <span>上传录音文件</span>
          </div>
          {/*
            不设 accept：iOS Files / 安卓 / 鸿蒙各家文件选择器对 accept 的解析行为互不一致，
            设了反而经常把合法 m4a/wav 灰掉甚至完全不弹选择器（鸿蒙 NEXT 微信 web-view 上 accept="audio/*" 会点击无响应）。
            统一交给 JS 里的 ALLOWED_EXTENSIONS 扩展名白名单做兜底校验。
          */}
          <input
            disabled={fileUping}
            type="file"
            onChange={this.fileChange}
          />
        </div>
      );
    }

    return (
      <div className="mini-recording-up">
        <ScrollToTopOnMount/>
        <Back/>
        <div className="guide-section">
          <div className="guide-title">
            <span className="guide-title-bar"></span>
            如何找到录音
          </div>
          <div className="guide-content">
            <div className="os-section">
              <p className="os-label">IOS：</p>
              <p>前置条件1：手机系统在ios 18以上；</p>
              <p>前置条件2：手机下载了苹果自带的【备忘录】这个app；</p>
              <p>1.通话时录音：挂断电话后，录音会自动存在【备忘录】。在备忘录中找到该记录后，可以长按录音，点击【将音频保存到"文件"】。上传时在文件夹找到对应文件即可上传。</p>
              <p>2.用"语音备忘录"APP录音：点击该条录音的右上角"···"- 分享 - 更多 -保存到文件。上传时在文件夹找到对应文件即可上传。</p>
            </div>
            <div className="os-section">
              <p className="os-label">安卓：</p>
              <p>小米 / 红米：MIUI → sound_recorder → call_rec；或 Android/data/com.miui.soundrecorder/files/Recordings</p>
              <p>华为 / 荣耀（鸿蒙 4）：Sounds → CallRecord（通话）、Sounds → Recordings（普通）</p>
              <p>鸿蒙 5：先在录音机把录音 "另存为" 到 Download，再进 Download 找</p>
              <p>vivo/iQOO：Recordings → Call</p>
            </div>
          </div>
        </div>
        {loading}
        <div className="bottom-area">
          <p className="format-tip">支持格式：mp3、wav、m4a，最大500MB</p>
          {btnBox}
        </div>
      </div>
    );
  }
}
